import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { toast } from 'sonner';
import { Sparkles, Check, ArrowRight } from 'lucide-react';
import MobileStickyBar from '../components/common/MobileStickyBar';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FABRICATION_TYPES = [
  { id: 'arche_ballon', label: 'Arche ballons organiques', icon: '🎈', desc: 'Décoration ballon sur-mesure, palette de couleurs au choix',
    options: [
      { id: 'couleurs', label: 'Couleurs', type: 'select', choices: ['Blanc/Or', 'Rose/Nude', 'Bleu ciel/Or', 'Vert sauge', 'Corail/Pêche', 'Personnalisé'] },
      { id: 'type_arche', label: 'Type', type: 'select', choices: ['Demi-arche (1m)', 'Arche complète (2m)', 'Colonne (1.8m)', 'Portique (2x2m)'] },
      { id: 'notes', label: 'Précisions', type: 'textarea', placeholder: 'Ex: couleurs pastel, tons champagne...' }
    ]
  },
  { id: 'panneau_personnalise', label: 'Panneau personnalisé', icon: '🎨', desc: 'Panneau bienvenue, date, prénoms en bois ou acrylique',
    options: [
      { id: 'texte', label: 'Texte à inscrire', type: 'text', placeholder: 'Ex: Sophie & Thomas — 12 Juin 2026' },
      { id: 'materiau', label: 'Matériau', type: 'select', choices: ['Bois naturel', 'Acrylique blanc', 'Acrylique noir', 'Ardoise'] },
      { id: 'format', label: 'Format', type: 'select', choices: ['A2 (42×60cm)', 'A1 (60×84cm)', 'Chevalet (100cm)'] },
      { id: 'couleur_texte', label: 'Couleur texte', type: 'select', choices: ['Or', 'Argent', 'Blanc', 'Noir', 'Rose', 'Teal'] }
    ]
  },
  { id: 'decoration_sur_mesure', label: 'Décoration thémée', icon: '✨', desc: 'Décoration complète sur votre thème et couleurs',
    options: [
      { id: 'theme', label: 'Thème', type: 'text', placeholder: 'Ex: bohème, champêtre, tropical...' },
      { id: 'couleurs', label: 'Palette de couleurs', type: 'text', placeholder: 'Ex: rose poudré, nude, or, blanc' },
      { id: 'ambiance', label: 'Ambiance souhaitée', type: 'textarea', placeholder: 'Décrivez vos inspirations...' }
    ]
  },
  { id: 'photo_cadre', label: 'Cadre photo personnalisé', icon: '📷', desc: 'Cadre pour photobooth ou fond de scène',
    options: [
      { id: 'texte', label: 'Texte sur le cadre', type: 'text', placeholder: 'Ex: Sophie & Thomas ♥ 12.06.2026' },
      { id: 'couleur', label: 'Couleur fond', type: 'select', choices: ['Blanc', 'Noir', 'Or', 'Rose pâle', 'Teal', 'Champagne'] },
      { id: 'format', label: 'Format', type: 'select', choices: ['10×15cm', '15×20cm', '20×30cm (poster)'] }
    ]
  },
  { id: 'creation_libre', label: 'Création libre', icon: '🎡', desc: 'Vous avez une idée ? Partagez-la, nous la réalisons.',
    options: [
      { id: 'description', label: 'Votre idée', type: 'textarea', placeholder: 'Décrivez votre création idéale, les matériaux, les couleurs...' }
    ]
  }
];

export default function FabricationPage() {
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState({ client_nom: '', client_email: '', client_telephone: '', date_evenement: '', budget_max: '', description: '', options: {} });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit() {
    if (!selectedType || !form.client_email) { toast.error('Sélectionnez un type et saisissez votre email'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/fabrication/demande`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type: selectedType.id })
      });
      const data = await res.json();
      if (data.success) { setSubmitted(true); toast.success(data.message); }
    } catch (e) { toast.error('Erreur envoi'); }
    setSubmitting(false);
  }

  if (submitted) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose flex items-center justify-center" style={{ minHeight: '70vh' }}>
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'var(--teal-mist)', border: '2px solid var(--teal)' }}>
            <Check size={36} style={{ color: 'var(--teal)' }} />
          </div>
          <h1 className="font-display text-2xl font-bold mb-3" style={{ color: 'var(--ink)' }}>Demande envoyée !</h1>
          <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>Nous vous répondons sous 48h avec un devis personnalisé.</p>
          <Link to="/" className="btn-teal px-6 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2">Retour à l'accueil <ArrowRight size={16} /></Link>
        </div>
      </div>
      <MobileStickyBar />
      <Footer />
    </div>
  );

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '4rem', maxWidth: '800px' }}>
        <div className="rounded-3xl p-8 mb-8 text-center" style={{ background: 'linear-gradient(135deg, var(--teal-mist) 0%, var(--rose-mist) 100%)', border: '1px solid rgba(93,188,191,0.2)' }}>
          <div className="text-4xl mb-3">✨</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>Vous imaginez — nous fabriquons</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>Partagez votre idée, nous créons une décoration ou animation 100% sur-mesure.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {FABRICATION_TYPES.map(t => (
            <button key={t.id} onClick={() => setSelectedType(t)}
              className="p-4 rounded-2xl text-left transition-all"
              style={{ background: selectedType?.id === t.id ? 'var(--teal)' : '#FFFFFF', border: `2px solid ${selectedType?.id === t.id ? 'var(--teal)' : 'var(--border)'}`, boxShadow: selectedType?.id === t.id ? '0 8px 24px rgba(93,188,191,0.2)' : '0 4px 12px rgba(26,26,46,0.05)' }}>
              <div className="text-2xl mb-2">{t.icon}</div>
              <p className="font-semibold text-sm mb-1" style={{ color: selectedType?.id === t.id ? '#0B1E20' : 'var(--ink)' }}>{t.label}</p>
              <p className="text-xs" style={{ color: selectedType?.id === t.id ? 'rgba(11,30,32,0.65)' : 'var(--text-muted)' }}>{t.desc}</p>
            </button>
          ))}
        </div>

        {selectedType && (
          <div className="rounded-2xl p-6 mb-6" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
            <h2 className="font-display font-bold text-lg mb-4" style={{ color: 'var(--ink)' }}>{selectedType.icon} {selectedType.label}</h2>
            <div className="space-y-4">
              {selectedType.options.map(opt => (
                <div key={opt.id}>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>{opt.label}</label>
                  {opt.type === 'select' ? (
                    <select value={form.options[opt.id] || ''} onChange={e => setForm(f => ({ ...f, options: { ...f.options, [opt.id]: e.target.value } }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                      <option value="">Choisir...</option>
                      {opt.choices?.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : opt.type === 'textarea' ? (
                    <textarea rows={3} placeholder={opt.placeholder} value={form.options[opt.id] || ''}
                      onChange={e => setForm(f => ({ ...f, options: { ...f.options, [opt.id]: e.target.value } }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm resize-none" style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                  ) : (
                    <input type="text" placeholder={opt.placeholder} value={form.options[opt.id] || ''}
                      onChange={e => setForm(f => ({ ...f, options: { ...f.options, [opt.id]: e.target.value } }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl p-6 mb-6" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="font-semibold text-base mb-4" style={{ color: 'var(--ink)' }}>Vos coordonnées</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { key: 'client_nom', label: 'Nom & prénom', placeholder: 'Jean Dupont' },
              { key: 'client_email', label: 'Email *', placeholder: 'jean@exemple.fr', type: 'email' },
              { key: 'client_telephone', label: 'Téléphone', placeholder: '06 12 34 56 78', type: 'tel' },
              { key: 'date_evenement', label: "Date de l'événement", type: 'date' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>{f.label}</label>
                <input type={f.type || 'text'} placeholder={f.placeholder} value={form[f.key] || ''}
                  onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>Budget maximum estimé (€)</label>
              <input type="number" placeholder="Ex: 300" value={form.budget_max || ''}
                onChange={e => setForm(p => ({ ...p, budget_max: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
            </div>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={submitting || !form.client_email || !selectedType}
          className="w-full btn-teal py-4 rounded-xl text-base font-bold flex items-center gap-2 justify-center disabled:opacity-40">
          <Sparkles size={18} />
          {submitting ? 'Envoi en cours...' : 'Envoyer ma demande de fabrication'}
        </button>
        <p className="text-xs text-center mt-3" style={{ color: 'var(--text-muted)' }}>Réponse sous 48h avec devis personnalisé.</p>
      </div>
      <Footer />
    </div>
  );
}
